// Pure functions — derive plan state from canonical Firestore shape.
// Never duplicate state in React; call these from components.

import type { Plan, TrialsUsed, PlanTier } from "./planTypes";
import { DEVICE_LIMITS, ULTIMATE_DESKTOP_LIMIT } from "./planTypes";

export function isPlanActive(plan: Plan | null | undefined): boolean {
  if (!plan) return false;
  if (plan.tier === "basic") return false;
  if (!plan.expiresAt) return false;
  return new Date(plan.expiresAt).getTime() > Date.now();
}

export function isInTrial(plan: Plan | null | undefined): boolean {
  if (!plan?.trial) return false;
  return new Date(plan.trial.endsAt).getTime() > Date.now();
}

export function daysRemaining(plan: Plan | null | undefined): number {
  if (!plan?.expiresAt) return 0;
  const diff = new Date(plan.expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
}

export function hoursRemaining(plan: Plan | null | undefined): number {
  if (!plan?.expiresAt) return 0;
  const diff = new Date(plan.expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 3600000));
}

export function canStartTrial(
  trialsUsed: TrialsUsed | null | undefined,
  _targetTier: "pro" | "ultimate",
): boolean {
  return !trialsUsed?.any;
}

export function canUseDesktop(plan: Plan | null | undefined): boolean {
  if (!plan) return false;
  return plan.tier === "ultimate" && isPlanActive(plan);
}

/** Authoritative tier: if expired, treat as basic regardless of stored tier. */
export function effectiveTier(plan: Plan | null | undefined): PlanTier {
  if (!plan) return "basic";
  return isPlanActive(plan) ? plan.tier : "basic";
}

export function deviceLimitForTier(tier: PlanTier): number {
  return DEVICE_LIMITS[tier];
}

export function desktopLimitForTier(tier: PlanTier): number {
  return tier === "ultimate" ? ULTIMATE_DESKTOP_LIMIT : 0;
}

export function isOneTimeUltimate(plan: Plan | null | undefined): boolean {
  return !!plan && plan.tier === "ultimate" && plan.oneTime !== null && plan.billing === null;
}

export function isRecurringSubscription(plan: Plan | null | undefined): boolean {
  return !!plan?.billing?.subscriptionId;
}

/** "Cancel at renewal" or immediate cancel — surfaces in BillingPage UI. */
export function willDowngradeSoon(plan: Plan | null | undefined): boolean {
  return !!plan?.billing?.willDowngradeAt && plan.billing.status === "cancelled_at_cycle_end";
}

export function isPastDue(plan: Plan | null | undefined): boolean {
  return plan?.billing?.status === "past_due";
}

export function isPaused(plan: Plan | null | undefined): boolean {
  return plan?.billing?.status === "paused";
}

export function planLabel(plan: Plan | null | undefined): string {
  const tier = effectiveTier(plan);
  if (tier === "basic") return "Basic";
  if (tier === "pro") return "Pro";
  return "Ultimate";
}

export function trialLabel(plan: Plan | null | undefined): string | null {
  if (!isInTrial(plan)) return null;
  const days = daysRemaining(plan);
  if (days === 0) return "Trial ends today";
  if (days === 1) return "Trial ends tomorrow";
  return `Trial ends in ${days} days`;
}

export function renewalLabel(plan: Plan | null | undefined): string | null {
  if (!plan?.billing?.nextChargeAt) return null;
  const d = new Date(plan.billing.nextChargeAt);
  return `Renews on ${d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`;
}
